import { FC } from "react";
import Logo from "designs/DesignB/components/Logo/Logo";
import { NavSlot, useNavFit } from "shared/Navigation/NavFit";
import MenuBar from "shared/MenuBar/MenuBar";
import { Link, useLocation } from "react-router-dom";
import { useData } from "data/data-provider";
import TopBarSearch from "designs/Common/TopBarSearch/TopBarSearch";
import { useTopBarSearchVisible } from "designs/Common/TopBarSearch/useTopBarSearchVisible";

export interface MainNav1Props {
  className?: string;
  /** The copy pinned to the top of the home page once the banner has scrolled away (1NOW-11153). */
  pinned?: boolean;
}

// Header layout variants, chosen in the FMS website builder and stored on
// CompanyWebsiteDetail.header_layout:
//   "classic"      → logo left, nav right, "Book Now" CTA pinned right; default
//   "split_cta"    → logo left, nav centered, "Book Now" CTA pinned right
//   "drawer_right" → logo left, "Book Now" CTA + menu button right; nav links
//                    live in a slide-out panel from the right (no inline nav)
//
// The "Book Now" CTA is the primary conversion action and is shown in every
// layout (1NOW-1240) — only its placement and the nav arrangement differ.
type HeaderVariant = "classic" | "split_cta" | "drawer_right";

/**
 * Design B desktop / mobile nav.
 *
 * One bar on every page (1NOW-11175): solid, in the tenant's footer colours, never
 * floating over the hero. The internal arrangement (logo / nav / CTA positions)
 * follows the chosen header layout variant; the mobile hamburger is shared
 * across every variant.
 */
const MainNav: FC<MainNav1Props> = ({ className = "", pinned = false }) => {
  // `!` on the show/hide variants below: see shared/Navigation/Navigation.tsx
  // (Meta Event Setup Tool injects a competing Tailwind sheet).
  const location = useLocation();
  const { companyDetails } = useData();
  // 1NOW-11153: the search bar in the top bar. It takes the nav's place on
  // desktop, so the menu button shows at every width while it is up.
  // On the home page the header inside the banner keeps its menu; the pinned copy carries the search.
  const searchVisible = useTopBarSearchVisible();
  const showSearch = searchVisible && (pinned || !["/", "/home"].includes(location.pathname));
  // 1NOW-11061: the inline nav (classic, split_cta) sits in a slot between the logo and the
  // right cluster and is shown only while it fits; otherwise the menu button lists every item.
  // The nav never overlaps the logo or Book Now, however many pages the host adds.
  const fit = useNavFit("xl");

  // Source of truth for the layout is the FMS-set `header_layout`. A
  // `?headerVariant=` query param overrides it for live preview (handy
  // before the tenant has saved a choice). Defaults to "classic" so every
  // existing tenant keeps the original logo-left / nav-right bar.
  const params = new URLSearchParams(location.search);
  const variant: HeaderVariant =
    (params.get("headerVariant") as HeaderVariant) ||
    (companyDetails?.header_layout as HeaderVariant) ||
    "classic";

  // Design B's bar (1NOW-11175): one solid bar on every page, in the footer's colours,
  // with the menu in the bold uppercase style, the accent rule under the current page
  // and a primary Book Now. Styled in DesignB/theme.css (.db-header).
  // Book Now is not in the phone bar (1NOW-11174 reopen, Taha 7 Oct): the pill and the logo take
  // the room, and Book Now stays at the top of the menu (NavMobile). `.db-btn` sets its own
  // `display`, which a plain `hidden` lost to, so the phone rule is `!hidden`; from sm up it shows.
  const bookNow = (
    <Link to="/vehicles" className="!hidden sm:!inline-flex db-btn db-btn--primary db-btn--sm">
      Book Now
    </Link>
  );
  // 1NOW-11192: the brand in the bar while the search is up, at every width (Taha 7 Oct: phones
  // too). The same Logo the full header and the footer draw: the uploaded image, else the tile and
  // the name, linking to "/". `compact` sizes it so the search bar and the menu keep one line:
  // on phones a small mark, the name only between lg and xl (see Logo).
  const searchBrand = (
    <div className="flex flex-shrink-0 items-center">
      <Logo compact />
    </div>
  );
  // The menu button wears the bar's colours and shows its own frame; `dialogClassName`
  // carries the theme scope into the drawer, which is portalled out of the page. The drawer
  // sits above the pinned home header (z-[60]): under it, the header covered the top of the
  // panel, its logo and its close button, and a tall logo showed from under the bar (1NOW-11181).
  const menuButton = (extra: string) => (
    <div ref={fit.menuRef} className={`flex flex-shrink-0 items-center ${extra}`}>
      <MenuBar className="db-menu-btn p-2.5" iconClassName="h-6 w-6" dialogClassName="design-b !z-[70]" />
    </div>
  );

  return (
    <div className={`nc-MainNav1 relative z-10 ${className}`}>
      <div className="db-header">
        {variant === "drawer_right" ? (
          /* Right nav panel: logo left, Book Now + menu button right. The menu button opens
             the slide-out panel (NavMobile) at ALL breakpoints, so the nav lives entirely in
             the drawer; the Book Now CTA stays in the bar (from sm up; phones have it in the menu). */
          <div className="container py-3 relative flex items-center justify-between gap-2 sm:gap-4">
            {/* Brand. While the search bar is up the logo sits left of it at every width, a small
                mark on phones (1NOW-11192, Taha 7 Oct). */}
            {!showSearch && (
              <div className="flex items-center">
                <Logo />
              </div>
            )}

            {showSearch && searchBrand}
            {showSearch && <TopBarSearch logo={<Logo />} compact />}

            {/* Right cluster: persistent Book Now CTA + menu trigger */}
            <div className="ml-auto flex items-center gap-3">
              {bookNow}
              {menuButton("justify-end")}
            </div>
          </div>
        ) : variant === "split_cta" ? (
          /* Split + CTA: logo left, nav centred in the flex slot between the logo and the CTA,
             Book Now right. The nav used to be `absolute left-1/2`, which slid it under the logo
             and the CTA once a host added many pages (1NOW-11061); now it shows only while it
             fits and the menu button takes over otherwise. Below xl it is always the hamburger
             (the CTA stays from sm up). */
          <div className="container py-3 relative flex items-center gap-2 sm:gap-4">
            {/* Brand. While the search bar is up the logo sits left of it at every width, a small
                mark on phones (1NOW-11192). */}
            {!showSearch && (
              <div className="flex flex-shrink-0 items-center">
                <Logo />
              </div>
            )}

            {/* Centered nav (while it fits), or the search bar in its place */}
            {showSearch && searchBrand}
            {showSearch ? <TopBarSearch logo={<Logo />} compact /> : <NavSlot fit={fit} align="center" />}

            {/* Right cluster: CTA (desktop/tablet) + mobile menu */}
            <div className="ml-auto flex items-center justify-end gap-3">
              {bookNow}
              {menuButton(showSearch ? "" : fit.menuClass)}
            </div>
          </div>
        ) : (
          /* Classic (default): logo left, nav + Book Now right. Plain `container` so the logo
             and nav share the gutter of the hero and every section below. The nav, the
             persistent Book Now (from sm up) and the menu trigger share one right-side
             cluster (1NOW-1240). */
          <div className="container py-3 relative flex items-center justify-between gap-2 sm:gap-4">
            {/* Brand. While the search bar is up the logo sits left of it at every width, a small
                mark on phones (1NOW-11192, Taha 7 Oct). */}
            {!showSearch && (
              <div className="flex items-center">
                <Logo />
              </div>
            )}

            {showSearch && searchBrand}
            {showSearch && <TopBarSearch logo={<Logo />} compact />}

            {/* Desktop nav, right-aligned beside the CTA while it fits (1NOW-11061) */}
            {!showSearch && <NavSlot fit={fit} align="end" />}

            {/* Right cluster: Book Now CTA + mobile menu */}
            <div className="ml-auto flex items-center gap-4">
              {bookNow}

              {menuButton(showSearch ? "justify-end" : `justify-end ${fit.menuClass}`)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MainNav;
